import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { getUserEHR } from '@/pages/api/scribeAPIV2';
import { type EHRProfileResponse } from '@/types';
import {
  getEhrConnectionErrorMessage,
  isEhrConnectionError,
} from '@/utils/ehr-connection-error';
import { shouldSuppressDesktopOfflineNetworkErrorToast } from '@/utils/http/desktopOfflineNetwork';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useQueryKindeProperties } from '../useQueryKindeProperties';
import { useQueryUserForContext } from '../useQueryUser';

// paths
export const GET_USER_EHR = 'GetUserEHR';

export function useQueryUserEHR<T = EHRProfileResponse>(
  select?: (data: EHRProfileResponse) => T
) {
  const { t } = useTranslations();
  const { isAuthenticated } = useIsAuthenticated();
  const { data: kindeProperties } = useQueryKindeProperties();
  const { data: user } = useQueryUserForContext((data) => data.kinde_user_id);

  return useQuery({
    queryKey: [GET_USER_EHR],
    enabled: isAuthenticated && !!kindeProperties?.region && !!user,
    select,
    queryFn: async () => {
      try {
        const { data } = await getUserEHR();
        return data;
      } catch (error) {
        if (isEhrConnectionError(error)) {
          toast({
            tone: 'destructive',
            title: t('ehr.ehrConnectionError'),
            description: getEhrConnectionErrorMessage(error),
          });
        } else if (error instanceof AxiosError) {
          // Never toast the raw payload — EHR responses can carry PHI/PII.
          // The raw error goes to the log channel only; the toast shows a
          // fixed, safe message that never echoes backend detail strings.
          logger.error('User EHR request failed', error);
          if (!shouldSuppressDesktopOfflineNetworkErrorToast(error)) {
            toast({
              tone: 'destructive',
              title: t('ehr.ehrConnectionError'),
              description: t('ehr.pleaseTryAgain'),
            });
          }
        }
        throw new Error();
      }
    },
  });
}
